import { useState, useCallback, useMemo, useContext, memo, useEffect } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import { capitalize, sumBy } from 'mojito/utils';
import EventListHeader from './header/index.js';
import EventListGroup from './group/index.js';
import EventListItem from './item/index.js';
import { useEventItem } from 'modules/common/hooks';
import {
    useMarketColumnCount,
    useMarketSwitchers,
    useEventsCouponPreload,
    useEventsCouponPreloadBatches,
} from './hooks';
import EventListUtils from './utils.js';
import { EventListContext } from 'modules/event-list/context/event-list-context.js';

const { FlexPane, ImageButton, InfiniteList } = MojitoPresentation.Components;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const IntentActionTrigger = MojitoPresentation.Base.Intent.IntentActionTrigger;
const { EVENT_TYPE } = MojitoServices.SportsContent.Events.types;
const log = MojitoCore.logger.get('EventList');

const TYPE_SORTING = {
    TIME: 'TIME',
    DEFAULT: 'DEFAULT',
};
const TIME_SORTED_GROUP_ID = 'TIME_SORTED_GROUP_ID';

export default memo(function EventList(props) {
    const {
        mojitoTools: { config, appContext, stringResolver },
        eventGroups,
        marketOptions,
        sportId,
        isInPlay,
        headerText,
        initialExpandedGroupIds,
        enableSorting,
    } = props;
    const { numberOfEvents } = useContext(EventListContext) || {};
    const {
        routeResolver,
        analyticsEmitter: { emitAnalytics },
    } = appContext;

    const getHrefAllEvents = () => {
        return isInPlay
            ? NavigationPathBuilder.inplayPath(sportId, routeResolver)
            : NavigationPathBuilder.sportPath(sportId, routeResolver);
    };

    const onShowAllEventsClick = () => {
        if (isInPlay) {
            IntentActionTrigger.showInPlay(sportId);
        } else {
            IntentActionTrigger.showSport(sportId);
        }
        props.onShowAllButtonClick(sportId);
    };

    const isGameLineMode = config.gameLineSports[sportId];
    const { count: marketColumnCount, elementRef } = useMarketColumnCount(
        config.marketsSection,
        isGameLineMode
    );

    const marketSwitcherLabels = useMemo(() => {
        const labels = marketOptions.map(getOptionLabel).filter(Boolean);
        if (labels.length !== marketOptions.length) {
            log.warn(`Labels are missing for some of ${sportId} market options.`);
        }
        return labels;
    }, [marketOptions, sportId]);

    const { onMarketSwitch, selectedSwitchers } = useMarketSwitchers(
        marketSwitcherLabels,
        marketColumnCount
    );

    const selectedMarketOptions = useMemo(
        () => findSelectedMarketOptions(selectedSwitchers, marketOptions),
        [selectedSwitchers, marketOptions]
    );

    const headerTypes = useMemo(() => {
        const optionMapper = isGameLineMode ? getGameLineHeaders : option => option?.headers;
        return selectedMarketOptions.map(optionMapper);
    }, [isGameLineMode, selectedMarketOptions]);

    const sportName = appContext.sportName(sportId);
    // Determining eventIds for expanded event groups to preload
    const eventIds = useMemo(() => {
        return eventGroups
            .filter(eventGroup => initialExpandedGroupIds.includes(eventGroup.id))
            .flatMap(eventGroup => eventGroup.events.map(event => event.id));
    }, [eventGroups, initialExpandedGroupIds]);

    const titleText = headerText || sportName;
    const { nothingToShow: hideHeader } = EventListUtils.resolveHeaderAppearance(
        config.header,
        titleText,
        marketSwitcherLabels,
        isGameLineMode,
        isInPlay,
        enableSorting
    );

    const [sortingType, setSortingType] = useState(TYPE_SORTING.DEFAULT);

    useEffect(() => {
        setSortingType(TYPE_SORTING.DEFAULT);
    }, [props.eventGroupsId]);

    const handleToggleSort = () => {
        if (sortingType === TYPE_SORTING.DEFAULT) {
            emitAnalytics('sortingClicked', { sortBy: 'time', sportId });
            setSortingType(TYPE_SORTING.TIME);
        } else {
            emitAnalytics('sortingClicked', { sortBy: 'default', sportId });
            setSortingType(TYPE_SORTING.DEFAULT);
        }
    };

    const sortedEvents = useMemo(() => {
        const events = eventGroups
            .flatMap(group => group.events)
            .sort((a, b) => {
                const timeComparison = new Date(a.startTime) - new Date(b.startTime);
                return timeComparison !== 0 ? timeComparison : a.id - b.id;
            });

        return [
            {
                id: TIME_SORTED_GROUP_ID,
                events,
            },
        ];
    }, [eventGroups]);

    const isTimeSorting = sortingType === TYPE_SORTING.TIME;
    const groups = isTimeSorting ? sortedEvents : eventGroups;
    const sportNameSorting =
        stringResolver.resolveString(`$SPORT_GROUPS_LIST.COMPETITIONS_NAME.${sportId}`, true) ||
        stringResolver.resolveString(`$SPORT_GROUPS_LIST.COMPETITIONS_NAME.DEFAULT`);
    const sortingTitle =
        sortingType === TYPE_SORTING.DEFAULT
            ? capitalize(sportNameSorting)
            : stringResolver.resolveString('$EVENT_LIST.TIME_BUTTON');

    const hideMarketHeaders = config.hideMarketHeaders.some(sport => sport === sportId);

    return (
        <FlexPane class="ta-EventList" config={config.container}>
            {!hideHeader && (
                <EventListHeader
                    config={config.header}
                    marketSectionRef={elementRef}
                    sportId={sportId}
                    titleText={titleText}
                    isGameLineMode={isGameLineMode}
                    isInPlay={isInPlay}
                    numberOfMarketSwitchers={marketColumnCount}
                    marketOptions={marketSwitcherLabels}
                    selectedMarketOptions={selectedSwitchers}
                    onSelectedMarketOptionChange={onMarketSwitch}
                    enableSorting={enableSorting}
                    showEarlyPayoutOffers={config.showEarlyPayoutInHeader}
                    eventGroups={eventGroups}
                    sortingTitle={sortingTitle}
                    onSortingClicked={handleToggleSort}
                />
            )}
            <FlexPane class="ta-EventListGroups" config={config.eventGroupsContainer}>
                {groups.map(group => (
                    <EventListGroup
                        key={group.id}
                        eventGroup={group}
                        sportId={group.linkedSportId || sportId}
                        config={config.eventListGroup}
                        headerTypes={
                            group.eventGroupType !== EVENT_TYPE.OUTRIGHT ? headerTypes : undefined
                        }
                        initiallyExpanded={
                            isTimeSorting || initialExpandedGroupIds.includes(group.id)
                        }
                        isTimeSorting={isTimeSorting}
                        showEarlyPayoutOffers={!config.showEarlyPayoutInHeader}
                        hideMarketHeaders={hideMarketHeaders}
                    >
                        <FlexPane class="ta-EventListItems" config={config.eventItemsContainer}>
                            {isTimeSorting ? (
                                <EventBatchesListContent
                                    {...props}
                                    events={group.events}
                                    selectedMarketOptions={selectedMarketOptions}
                                    isGameLineMode={isGameLineMode}
                                />
                            ) : (
                                <EventListContent
                                    {...props}
                                    sportId={group.linkedSportId || sportId}
                                    groupSportId={sportId}
                                    events={group.events}
                                    eventIds={eventIds}
                                    selectedMarketOptions={selectedMarketOptions}
                                    isGameLineMode={isGameLineMode}
                                />
                            )}
                        </FlexPane>
                    </EventListGroup>
                ))}
            </FlexPane>
            {config.showAllEventsButton && (
                <ImageButton
                    onClick={onShowAllEventsClick}
                    hrefLink={getHrefAllEvents()}
                    label={resolveAllEventsLabel(
                        sportName,
                        isInPlay,
                        numberOfEvents || getTotalEventCount(eventGroups),
                        stringResolver
                    )}
                    config={config.allEventsButton}
                />
            )}
        </FlexPane>
    );
});

const EventListContent = props => {
    const {
        events,
        eventGroups,
        selectedMarketOptions,
        eventIds,
        isGameLineMode,
        onDataLoad,
        mojitoTools: { instanceId },
    } = props;
    const preloadDone = useEventsCouponPreload(
        instanceId,
        eventIds,
        eventGroups,
        selectedMarketOptions,
        isGameLineMode
    );

    useEffect(() => {
        preloadDone && onDataLoad(eventGroups);
    }, [preloadDone, eventGroups, onDataLoad]);

    return events.map(eventInfo => (
        <Event
            {...props}
            key={eventInfo.id}
            eventInfo={eventInfo}
            shouldRequestData={preloadDone}
        />
    ));
};

const EventBatchesListContent = props => {
    const {
        events,
        eventGroups,
        selectedMarketOptions,
        isGameLineMode,
        onDataLoad,
        mojitoTools: { config, instanceId },
    } = props;
    const [hasMore, setHasMore] = useState(true);
    const { visibleEvents, onFetchMore, preloadDone } = useEventsCouponPreloadBatches(
        instanceId,
        eventGroups,
        selectedMarketOptions,
        isGameLineMode,
        events,
        () => setHasMore(false),
        config.batchSize
    );
    useEffect(() => {
        preloadDone && onDataLoad(eventGroups);
    }, [preloadDone, eventGroups, onDataLoad]);

    return (
        <div id="resultsContainer">
            <InfiniteList
                fetchMore={onFetchMore}
                hasMore={hasMore}
                config={config.infiniteListItemsContainer}
            >
                {visibleEvents.map(eventInfo => (
                    <Event
                        {...props}
                        key={eventInfo.id}
                        eventInfo={eventInfo}
                        shouldRequestData={preloadDone}
                    />
                ))}
            </InfiniteList>
        </div>
    );
};

const Event = props => {
    const {
        eventInfo,
        onItemClick,
        isGameLineMode,
        selectedMarketOptions,
        shouldRequestData,
        onChildDataLoad,
        sportId,
        groupSportId,
        isInPlay,
        isLiveAndFuture,
        mojitoTools: { config, appContext },
    } = props;
    const { routeResolver } = appContext;

    const eventItem = useEventItem(eventInfo.id, false);
    const eventIsInPlay = isLiveAndFuture ? Boolean(eventItem?.details?.inPlay) : isInPlay;

    const onEventClick = useCallback(
        (eventId, eventItem) => {
            const clickedEventIsInPlay = isLiveAndFuture
                ? Boolean(eventItem?.details?.inPlay)
                : isInPlay;

            if (clickedEventIsInPlay) {
                IntentActionTrigger.showInPlayEvent(eventId);
            } else {
                IntentActionTrigger.showSportEvent(sportId, eventId);
            }
            onItemClick(eventId);
        },
        [sportId, isInPlay, isLiveAndFuture, onItemClick]
    );

    const getHrefItem = eventId => {
        return eventIsInPlay
            ? NavigationPathBuilder.inplayEventPath(eventId, routeResolver)
            : NavigationPathBuilder.sportEventPath(sportId, eventId, routeResolver);
    };

    return (
        <EventListItem
            key={eventInfo.id}
            eventId={eventInfo.id}
            marketLines={eventInfo.marketLines}
            hrefLink={getHrefItem(eventInfo.id)}
            config={config.eventItem}
            onClick={onEventClick}
            isGameLineMode={isGameLineMode}
            marketOptions={selectedMarketOptions}
            shouldRequestData={shouldRequestData}
            onDataLoad={onChildDataLoad}
            groupSportId={groupSportId}
        />
    );
};

const getTotalEventCount = groups => sumBy(groups, group => group.events.length);
const getGameLineHeaders = gameLineOption => gameLineOption.options?.map(getOptionLabel);

const findSelectedMarketOptions = (optionLabels, marketOptions) => {
    if (!optionLabels || !marketOptions) {
        return;
    }
    return optionLabels
        .map(label => marketOptions.find(option => getOptionLabel(option) === label))
        .filter(Boolean);
};

const resolveAllEventsLabel = (sportName, isInPlay, eventCount, stringResolver) => {
    const l10nKey = isInPlay
        ? `$EVENT_LIST.ALL_INPLAY_EVENTS_BUTTON`
        : `$EVENT_LIST.ALL_EVENTS_BUTTON`;
    return stringResolver.resolveAndFormatString(l10nKey, sportName, eventCount);
};

const getOptionLabel = option => option.name || option.label;
